import { Button, Grid } from '@mui/material';
import EventSessionsTable from 'components/events/EventSessionsTable';
import PaymentAddressSection from 'components/payments/PaymentFormSection/PaymentAddressSection';
import PaymentMethodSection from 'components/payments/PaymentMethodSection';
import Modal from 'components/shared/Modal';
import { useSnackbar } from 'notistack';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import EventService from 'services/api/EventService';
import { BasePaymentData } from 'services/api/PaymentService';
import ManageEventSessionsMode from 'store/enums/ManageCourseSessionsMode';
import CollectJSResponse from 'store/types/CollectJSResponse';
import { ModalProps } from 'store/types/ComponentProps';
import EventSession, { EventSessionView } from 'store/types/EventSession';
import { PaymentFormValues } from 'store/types/FormValues';
import { defaultGridContainerProps, defaultGridItemProps } from 'util/Layout';
import { defaultPaymentFormValues, getPaymentData, getPrice } from 'util/Payment';
import { PaymentMethodsContext } from 'components/PaymentMethodsContextWrapper';
import { EventCartContext } from 'components/EventCartGuard';
import { getEventPriceLevel, getEventSessionsView } from 'util/Event';
import { UserContext } from 'components/UserGuard';
import { ParametersContext } from 'components/ParametersGuard';

import commonStyles from 'styles/common.module.scss';
import styles from './ManageEventSessionsModal.module.scss';

interface ManageEventSessionsModalProps extends ModalProps {
  id: string;
  data: EventSession[];
  guestCustomerId?: string;
  mode: ManageEventSessionsMode;
}

const ManageEventSessionsModal: React.FunctionComponent<ManageEventSessionsModalProps> = ({
  open,
  onClose,
  id,
  mode,
  data = [],
  guestCustomerId,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const {
    events: { sessionSelectionModalHeader },
  } = useContext(ParametersContext);
  const { refetch: refetchPaymentMethods } = useContext(PaymentMethodsContext);
  const [step, setStep] = useState<1 | 2>(1);
  const { priceLevelId } = useContext(UserContext);
  const { refetchCart } = useContext(EventCartContext);
  const [paymentForm, setPaymentForm] = useState<PaymentFormValues>(defaultPaymentFormValues);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [sessions, setSessions] = useState<EventSessionView[]>([]);
  const [selectedSessions, setSelectedSessions] = useState<EventSessionView[]>([]);
  const { totalPrice } = paymentForm;

  useEffect(() => {
    setPaymentForm((prevState) => ({
      ...prevState,
      totalPrice: selectedSessions.reduce(
        (prevValue, { prices, guestCustomerId }) =>
          prevValue + getEventPriceLevel(prices, priceLevelId, !!guestCustomerId),
        0
      ),
    }));
  }, [priceLevelId, selectedSessions]);

  useEffect(() => {
    if (data?.length) {
      setSessions(getEventSessionsView(data, priceLevelId));
    }
  }, [data, priceLevelId]);

  const handleSessionsChange = useCallback((newSelectedSessions: EventSessionView[] = []) => {
    setSelectedSessions(newSelectedSessions);
  }, []);

  const handlePaymentFormChange = useCallback((values: Partial<PaymentFormValues>) => {
    setPaymentForm((prevState) => ({ ...prevState, ...values }));
  }, []);

  const handleNextStep = useCallback(() => {
    setStep(2);
  }, []);

  const handlePaymentComplete = (response: CollectJSResponse) => {
    updateRegisteredSessions(response);
  };

  const updateRegisteredSessions = useCallback(
    (collectJSResponse?: CollectJSResponse) => {
      const selectedSessionIds = selectedSessions.map((session) => session.id);
      const basePaymentData: BasePaymentData = getPaymentData(paymentForm, collectJSResponse);

      setSubmitLoading(true);
      EventService.updateRegisteredEventSessions(
        id,
        selectedSessionIds,
        paymentForm.totalPrice ? basePaymentData : undefined,
        guestCustomerId
      )
        .then(() => {
          refetchPaymentMethods();
          setSubmitLoading(false);
          enqueueSnackbar('Sessions successfully updated', { variant: 'success' });
          refetchCart();
          onClose();
        })
        .catch((errorMessage: string) => {
          setSubmitLoading(false);
          enqueueSnackbar(errorMessage, { variant: 'error' });
        });
    },
    [selectedSessions, paymentForm, id, guestCustomerId, refetchPaymentMethods, enqueueSnackbar, refetchCart, onClose]
  );

  const addToCart = useCallback(
    (sessionsSelected = false) => {
      const selectedSessionIds = sessionsSelected ? selectedSessions.map((session) => session.id) : undefined;

      setSubmitLoading(true);
      EventService.addEventToCart(id, selectedSessionIds, guestCustomerId)
        .then(() => {
          setSubmitLoading(false);
          enqueueSnackbar('Event was added to cart', {
            variant: 'success',
            anchorOrigin: { horizontal: 'center', vertical: 'top' },
          });
          refetchCart();
          onClose();
        })
        .catch((errorMessage: string) => {
          setSubmitLoading(false);
          enqueueSnackbar(errorMessage, { variant: 'error', anchorOrigin: { horizontal: 'center', vertical: 'top' } });
        });
    },
    [selectedSessions, id, guestCustomerId, enqueueSnackbar, refetchCart, onClose]
  );

  const handleEditInRegistrationClick = useCallback(() => {
    updateRegisteredSessions();
  }, [updateRegisteredSessions]);

  const handleAddLaterClick = useCallback(() => {
    addToCart();
  }, [addToCart]);

  const handleAddToCartClick = useCallback(() => {
    addToCart(true);
  }, [addToCart]);

  const handleUpdateInCartClick = useCallback(() => {
    const selectedSessionIds = selectedSessions.map((session) => session.id);

    setSubmitLoading(true);
    EventService.updateCartEventSessions(id, selectedSessionIds, guestCustomerId)
      .then(() => {
        setSubmitLoading(false);
        enqueueSnackbar('Event sessions successfully updated', { variant: 'success' });
        refetchCart();
        onClose();
      })
      .catch((errorMessage: string) => {
        setSubmitLoading(false);
        enqueueSnackbar(errorMessage, { variant: 'error' });
      });
  }, [selectedSessions, id, guestCustomerId, enqueueSnackbar, refetchCart, onClose]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      maxWidth={'lg'}
      title={step === 1 ? 'Session Selection' : `Session Payment: ${getPrice(totalPrice)}`}
      loading={submitLoading}
      keepMounted={mode === ManageEventSessionsMode.AddToCart}
      actions={
        mode === ManageEventSessionsMode.EditRegistration ? (
          <Button
            color={'secondary'}
            variant={'contained'}
            type={'submit'}
            className={styles.submitButton}
            onClick={step === 1 ? handleNextStep : handleEditInRegistrationClick}
            disabled={submitLoading || !selectedSessions.length || totalPrice < 0}
          >
            <span>{'Update Selection'}</span>
            {totalPrice !== 0 && (
              <span className={styles.priceLabel}>{`${totalPrice > 0 ? '+' : '-'}(${getPrice(totalPrice)})`}</span>
            )}
          </Button>
        ) : (
          <>
            {mode === ManageEventSessionsMode.AddToCart && (
              <Button
                color={'secondary'}
                variant={'outlined'}
                onClick={handleAddLaterClick}
                disabled={submitLoading}
                className={styles.mainEventOnlyButton}
              >
                {'Main Event Only'}
              </Button>
            )}
            {mode === ManageEventSessionsMode.UpdateInCart ? (
              <Button
                color={'secondary'}
                variant={'contained'}
                type={'submit'}
                className={styles.submitButton}
                onClick={handleUpdateInCartClick}
                disabled={submitLoading}
              >
                {'Update Selection'}
              </Button>
            ) : (
              <Button
                color={'secondary'}
                variant={'contained'}
                type={'submit'}
                className={styles.submitButton}
                onClick={handleAddToCartClick}
                disabled={submitLoading || !selectedSessions.length}
              >
                {'Complete Selection'}
              </Button>
            )}
          </>
        )
      }
    >
      <Grid {...defaultGridContainerProps}>
        <Grid {...defaultGridItemProps}>
          <p dangerouslySetInnerHTML={{ __html: sessionSelectionModalHeader }} />
        </Grid>
        {mode !== ManageEventSessionsMode.EditRegistration || step === 1 ? (
          <Grid {...defaultGridItemProps}>
            <EventSessionsTable data={sessions} onChange={handleSessionsChange} />
          </Grid>
        ) : (
          <>
            <Grid {...defaultGridItemProps} lg={8}>
              <PaymentAddressSection form={paymentForm} onChange={handlePaymentFormChange} onlySavedAddresses={true} />
            </Grid>
            <Grid {...defaultGridItemProps} lg={4}>
              <h3 className={commonStyles.subTitle}>Payment Information</h3>
              <PaymentMethodSection
                form={paymentForm}
                onPaymentComplete={handlePaymentComplete}
                onChange={handlePaymentFormChange}
              />
            </Grid>
          </>
        )}
      </Grid>
    </Modal>
  );
};
export default ManageEventSessionsModal;
